/**
 * SystemHealth.tsx — Overview / System Health page (Closr style)
 *
 * Worker counts are derived from the live runs list. Infra metrics that the
 * platform does not expose to the tenant API (API uptime, DB pool, latencies)
 * are shown as "—" rather than fabricated. The scraping chart is real.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system). Stock text-2xl stays for Phase 1.
 */
import { CheckCircle2 } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState } from '../DashboardShared'
import { WorkspaceMetric } from './OverviewHome'
import { useAsync, fetchScrapingHealth, fetchRuns } from '../api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { accent, danger, ink, shadow, surface, warning } from '../../../theme/tokens'

export function SystemHealth() {
  const { data, loading, error } = useAsync(
    () => Promise.all([fetchScrapingHealth(7), fetchRuns(200)]),
    [],
  )

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const [health, runs] = data ?? [{ days: [], summary: { date: '', ok: 0, blocked: 0, error: 0, total: 0, successRate: 0 } }, []]
  const running = runs.filter(r => r.status === 'running').length
  const queued = runs.filter(r => r.status === 'queued' || r.status === 'admitted').length

  return (
    <div className="page-enter flex flex-col gap-5">
      <SectionTitle title="System Health" subtitle="Live run activity and scraping success rate" />

      <div className="grid grid-cols-4 gap-3">
        <WorkspaceMetric label="Runs Running" value={running} />
        <WorkspaceMetric label="Runs Queued" value={queued} />
        <WorkspaceMetric label="Scrapes OK (7d)" value={health.summary.ok} />
        <WorkspaceMetric label="Scrape Success" value={`${(health.summary.successRate * 100).toFixed(1)}%`} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* API Status — not instrumented on the tenant API */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">API Status</div>
              <div className="k-card-sub">Component-level latency is not exposed to the tenant API</div>
            </div>
          </div>
          <div className="p-5 flex flex-col gap-1">
            {['Control Plane API', 'Worker Queue (NATS)', 'Database (Postgres)', 'LinkedIn Proxy'].map(label => (
              <div key={label} className="flex items-center justify-between py-3 border-b border-edge-row-faint last:border-0">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-success" />
                  <span className="text-13 text-ink-primary">{label}</span>
                </div>
                <span className="text-12 font-mono text-ink-faint">—</span>
              </div>
            ))}
          </div>
        </div>

        {/* Worker Status — derived from runs */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Worker Status</div>
              <div className="k-card-sub">Derived from live runs</div>
            </div>
          </div>
          <div className="p-5 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Runs Running', value: running, color: accent.DEFAULT },
                { label: 'Runs Queued', value: queued, color: warning.DEFAULT },
              ].map(w => (
                <div key={w.label} className="bg-surface-input rounded-control p-4 border border-edge-row">
                  <div className="text-2xl font-bold tabular-nums" style={{ color: w.color }}>{w.value}</div>
                  <div className="text-11 text-ink-faint mt-1">{w.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scraping health chart */}
      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Scraping Health — 7 Days</div>
            <div className="k-card-sub">LinkedIn & web scraping success rate</div>
          </div>
        </div>
        <div className="p-5">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={health.days} barSize={18} barGap={2}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: ink.faint }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: ink.faint }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }} cursor={{ fill: surface['row-hover'] }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="ok" name="OK" fill={accent.soft} stackId="a" />
              <Bar dataKey="blocked" name="Blocked" fill={warning.DEFAULT} stackId="a" />
              <Bar dataKey="error" name="Error" fill={danger.DEFAULT} stackId="a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
